import React, { useEffect } from "react";
import { Pencil, Trash2 } from "lucide-react";

/**
 * Bottom-sheet detail modal (artifact `.overlay/.modal`).
 * Slides from the bottom on mobile, centered dialog on ≥sm screens.
 *
 * props:
 *  - open, onClose
 *  - title, sub, amount, amountColor (css color string)
 *  - rows: [[key, value], ...]
 *  - onEdit?: fn — shows "Düzenle"
 *  - onDelete?: fn — shows "Sil" (already permission-gated by caller)
 */
const FinanceDetailSheet = ({
  open,
  onClose,
  title,
  sub,
  amount,
  amountColor,
  rows = [],
  onEdit,
  onDelete,
}) => {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fin fixed inset-0 z-[90] flex items-end sm:items-center justify-center bg-[rgba(16,26,22,0.55)] backdrop-blur-[2px] fin-sheet-overlay"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        className="fin-sheet w-full sm:max-w-md max-h-[85vh] overflow-y-auto no-scrollbar bg-[var(--fin-card)] rounded-t-[22px] sm:rounded-[22px] px-5 pt-4 pb-8 sm:pb-6 pb-safe shadow-2xl"
      >
        {/* grab handle */}
        <div className="w-10 h-1 rounded-full bg-[var(--fin-track)] mx-auto mb-4 sm:hidden" />

        <h3 className="fin-grot text-[16px] font-semibold text-[var(--fin-text)] mb-0.5 break-words">
          {title}
        </h3>
        {sub && <div className="text-[12px] text-[var(--fin-muted)] mb-3">{sub}</div>}

        {amount != null && (
          <div
            className="fin-grot fin-num font-semibold text-[30px] mb-4"
            style={{ color: amountColor || "var(--fin-text)" }}
          >
            {amount}
          </div>
        )}

        <div>
          {rows.map(([k, v]) => (
            <div
              key={k}
              className="flex justify-between gap-4 py-[9px] border-b border-[var(--fin-row-line)] text-[13px]"
            >
              <span className="text-[var(--fin-muted)] shrink-0">{k}</span>
              <span className="font-semibold text-right text-[var(--fin-text)] break-words min-w-0">
                {v}
              </span>
            </div>
          ))}
        </div>

        <div className="flex gap-2.5 mt-5">
          {onEdit && (
            <button
              onClick={onEdit}
              className="flex-1 flex items-center justify-center gap-1.5 font-semibold text-[13px] py-3 rounded-xl border border-[var(--fin-line)] bg-[var(--fin-chipbg)] text-[var(--fin-text)] hover:bg-[var(--fin-hover)] transition-colors"
            >
              <Pencil className="w-3.5 h-3.5" /> Düzenle
            </button>
          )}
          {onDelete && (
            <button
              onClick={onDelete}
              className="flex-1 flex items-center justify-center gap-1.5 font-semibold text-[13px] py-3 rounded-xl border border-[color:var(--fin-neg-soft)] bg-[var(--fin-neg-soft)] text-[var(--fin-neg)] hover:opacity-80 transition-opacity"
            >
              <Trash2 className="w-3.5 h-3.5" /> Sil
            </button>
          )}
          <button
            onClick={onClose}
            className="flex-1 font-semibold text-[13px] py-3 rounded-xl bg-[var(--fin-net-bg)] text-[var(--fin-net-fg)] hover:opacity-90 transition-opacity"
          >
            Kapat
          </button>
        </div>
      </div>
    </div>
  );
};

export default FinanceDetailSheet;
